/**
 * ArtifactParser - Detecta blocos de código com linguagem artifact-* no DOM.
 *
 * Após o markdown-it renderizar o HTML, este parser varre os blocos <pre><code>
 * e identifica os que possuem classe `language-artifact-*`.
 *
 * Retorna uma lista de ArtifactBlock contendo tipo, código-fonte e elemento DOM.
 */

export interface ArtifactBlock {
    /** Tipo do artifact: 'react', 'html', 'mermaid', 'json', 'python' */
    type: string;
    /** Código-fonte bruto extraído do bloco */
    source: string;
    /** Elemento <pre> original que será substituído pelo artifact */
    element: HTMLElement;
    /** Se deve renderizar em modo harmonioso (sem chrome de dev) */
    seamless?: boolean;
}

/** Tipos de artifact suportados pelo frontend */
const SUPPORTED_TYPES = ['react', 'html', 'mermaid', 'json', 'python'];

export class ArtifactParser {
    /**
     * Varre um container DOM e extrai todos os blocos artifact-*.
     *
     * Procura por:
     *   <pre><code class="language-artifact-react">...</code></pre>
     *
     * Ou se já foi wrappado pelo processCodeBlocks():
     *   <div class="code-block-wrapper"><div class="code-block-header">...</div><pre><code class="language-artifact-react">...</code></pre></div>
     *
     * @param container Elemento DOM a ser varrido
     * @returns Lista de ArtifactBlock encontrados
     */
    static parse(container: HTMLElement): ArtifactBlock[] {
        const blocks: ArtifactBlock[] = [];
        const codeElements = container.querySelectorAll('code[class*="language-artifact-"]');

        codeElements.forEach((codeEl) => {
            const langClass = Array.from(codeEl.classList).find(c => c.startsWith('language-artifact-'));
            if (!langClass) return;

            let type = langClass.replace('language-artifact-', '').toLowerCase();
            let seamless = false;

            if (type.endsWith('-user')) {
                type = type.replace('-user', '');
                seamless = true;
            }

            if (!SUPPORTED_TYPES.includes(type)) return;

            const source = (codeEl.textContent || '').trim();
            if (!source) return;

            // O element a substituir é o wrapper (se existir) ou o <pre>
            const preEl = codeEl.closest('pre');
            if (!preEl) return;

            const wrapper = preEl.closest('.code-block-wrapper');
            const targetElement = (wrapper || preEl) as HTMLElement;

            // Evita processar o mesmo bloco duas vezes
            if (targetElement.dataset.artifactProcessed === 'true') return;
            targetElement.dataset.artifactProcessed = 'true';

            blocks.push({
                type,
                source,
                element: targetElement,
                seamless,
            });
        });

        return blocks;
    }

    /**
     * Verifica se um tipo de artifact é suportado.
     */
    static isSupported(type: string): boolean {
        return SUPPORTED_TYPES.includes(type.toLowerCase());
    }

    /**
     * Retorna a lista de tipos suportados.
     */
    static getSupportedTypes(): string[] {
        return [...SUPPORTED_TYPES];
    }
}
